import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Quote } from "lucide-react";
import { useLang } from "@/i18n";
import { Reveal, SectionHead } from "./Reveal";

export const Historia = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section ref={ref} id="historia" data-testid="historia-section" className="relative bg-tropical-light py-24 lg:py-36">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead id="historia" eyebrow={t.historia.eyebrow} title={t.historia.title} />
        <Reveal delay={0.05}>
          <p className="mt-6 font-display italic text-2xl sm:text-3xl text-berenjena max-w-2xl" data-testid="historia-lead">{t.historia.lead}</p>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <Reveal>
              <div className="relative overflow-hidden rounded-[2rem] aspect-[4/5] bg-jungle border-[6px] border-jungle">
                <motion.img style={{ y: imgY }} src="/img/local.jpg" alt="La barra de El gato con copas" className="absolute inset-0 h-[116%] w-full object-cover -top-[8%]" data-testid="historia-image" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <ol className="relative border-l-2 border-berenjena/30 ml-2" data-testid="historia-timeline">
              {t.historia.scenes.map((s, i) => (
                <li key={s.y + i} className="relative pl-8 sm:pl-10 pb-12 last:pb-0" data-testid={`historia-scene-${i}`}>
                  <span className="absolute -left-[9px] top-1.5 h-4 w-4 rounded-full bg-berenjena ring-4 ring-[#e4d8be]" />
                  <Reveal delay={0.05}>
                    <p className="font-mono-ed text-xs uppercase tracking-[0.25em] text-berenjena">0{i + 1} · {s.y}</p>
                    <h3 className="font-display text-2xl sm:text-3xl mt-2 leading-tight">{s.t}</h3>
                    <p className="mt-3 text-base sm:text-lg leading-relaxed text-jungle/80">{s.d}</p>
                  </Reveal>
                </li>
              ))}
            </ol>
            <Reveal delay={0.1}>
              <blockquote className="mt-14 relative rounded-[2rem] bg-paper/80 backdrop-blur p-8 sm:p-10 border border-jungle/10">
                <Quote className="text-berenjena" size={32} />
                <p className="font-display italic text-3xl sm:text-4xl mt-4 leading-tight" data-testid="historia-quote">{t.historia.quote}</p>
                <p className="mt-5 font-neon text-2xl text-berenjena" data-testid="historia-signature">{t.historia.signature}</p>
                <p className="mt-6 pt-5 border-t border-jungle/15 text-sm text-jungle/75" data-testid="historia-wink">{t.historia.wink}</p>
              </blockquote>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
